/**
 * Intercepts add-to-cart before theme handlers when fake cart is active.
 * Loaded synchronously in <head> immediately after fake-cart-store.js.
 */
(function () {
  if (!window.FakeCart) return;

  const FORM_SELECTOR = ['form[data-type="add-to-cart-form"]', "form.add-to-cart-form"].join(", ");

  function isInterceptableForm(form) {
    if (!form || form.tagName !== "FORM") return false;
    if (!form.matches(FORM_SELECTOR)) return false;
    if (form.closest("cart-drawer, fake-cart-drawer, .cart-drawer")) return false;
    if (form.classList.contains("free-product-form")) return false;
    if (form.id === "CartDrawer-Form") return false;
    if (form.closest(".upsell-form, .free-product-form")) return false;

    const submitter = form.querySelector('[type="submit"][name="add"], [type="submit"].add-to-cart-btn, [type="submit"].shop-add-to-cart-button, [type="submit"].gb-sumbit-free');
    if (submitter?.disabled) return false;

    return Boolean(form.querySelector('[name="id"], [name^="items["]'));
  }

  function resolveQuantity(form) {
    const formId = form.getAttribute("id");
    if (formId) {
      const linked = document.querySelector(
        `input[name="quantity"][form="${formId}"], .modern-quantity-selector .quantity-input[form="${formId}"]`,
      );
      if (linked) return Math.max(1, parseInt(linked.value, 10) || 1);
    }

    const inForm = form.querySelector('input[name="quantity"]');
    if (inForm && !inForm.disabled) return Math.max(1, parseInt(inForm.value, 10) || 1);

    const nearby = form
      .closest(".shop-product-info, .product-info, .product-form, .shop-add-to-cart-wrapper")
      ?.querySelector('input[name="quantity"], .quantity-input, .quantity__input');
    if (nearby) return Math.max(1, parseInt(nearby.value, 10) || 1);

    return 1;
  }

  function readItemMetadata(form, submitter) {
    if (window.FakeCartMetadata) {
      const fromButton = FakeCartMetadata.readFromElement(submitter);
      if (fromButton) return fromButton;

      const fromForm = FakeCartMetadata.readFromElement(form);
      if (fromForm) return fromForm;

      const fromTree = FakeCartMetadata.readMetadata(form);
      if (fromTree) return fromTree;
    }

    return scrapeProductMetadata(form);
  }

  function scrapeProductMetadata(form) {
    const root =
      form.closest(
        ".master-collection-grid__item, .custom-product-slide, .product-card-wrapper, .card-wrapper, .product-card, .collection-grid-page-product-card, .shop-product-info, .product-grid__item",
      ) || form;

    const titleEl = root.querySelector(
      ".product-title, .card__heading a, .card__heading, .shop-product-title, .full-unstyled-link",
    );
    const priceEl = root.querySelector(
      "[data-mgrid-selling-cents], .mgrid-selling-price, .sale-price, .price-item--sale, .price .price-item:last-child",
    );

    let price = 0;
    if (priceEl?.dataset?.mgridSellingCents) {
      price = parseInt(priceEl.dataset.mgridSellingCents, 10) || 0;
    } else if (priceEl?.textContent) {
      const match = priceEl.textContent.replace(/[^0-9.]/g, "");
      price = Math.round(parseFloat(match) * 100) || 0;
    }

    const compareEl = root.querySelector(
      ".collection-regular-price, .regular-price.line-through, .price-item--regular, s.price-item",
    );
    let compare_at_price = 0;
    if (compareEl?.dataset?.compareCents) {
      compare_at_price = parseInt(compareEl.dataset.compareCents, 10) || 0;
    } else if (compareEl?.textContent && price > 0) {
      const match = compareEl.textContent.replace(/[^0-9.]/g, "");
      compare_at_price = Math.round(parseFloat(match) * 100) || 0;
    }
    if (compare_at_price <= price) compare_at_price = 0;

    const imageEl = root.querySelector("img");
    const linkEl = root.querySelector('a.product-link, a[href*="/products/"]');

    return {
      title: titleEl?.textContent?.trim() || "",
      price,
      compare_at_price,
      image: imageEl?.currentSrc || imageEl?.src || "",
      url: linkEl?.href || "",
    };
  }

  function scrapeEyQtyMetadata(wrapper) {
    const card =
      wrapper.closest(
        ".collection-grid-page-product-card, .product-card-wrapper, .card-wrapper, [data-product-id]",
      ) || wrapper;

    const titleEl = card.querySelector(".product-title, .card__heading, h3, h2");
    const priceEl = card.querySelector("[data-sl-attribute-p], .sale-price, .price-item--sale, .mgrid-selling-price");

    let price = 0;
    if (priceEl?.dataset?.mgridSellingCents) {
      price = parseInt(priceEl.dataset.mgridSellingCents, 10) || 0;
    } else if (priceEl?.textContent) {
      const match = priceEl.textContent.replace(/[^0-9.]/g, "");
      price = Math.round(parseFloat(match) * 100) || 0;
    }

    const compareEl = card.querySelector(".collection-regular-price, .regular-price.line-through");
    let compare_at_price = 0;
    if (compareEl?.textContent && price > 0) {
      const match = compareEl.textContent.replace(/[^0-9.]/g, "");
      compare_at_price = Math.round(parseFloat(match) * 100) || 0;
    }
    if (compare_at_price <= price) compare_at_price = 0;

    return {
      title: titleEl?.textContent?.trim() || "",
      price,
      compare_at_price,
      image: card.querySelector("img")?.currentSrc || card.querySelector("img")?.src || "",
      url: card.querySelector('a[href*="/products/"]')?.href || "",
    };
  }

  function parseFormLineItems(form) {
    const fd = new FormData(form);
    const indices = new Set();

    form.querySelectorAll('[name^="items["]').forEach((el) => {
      const match = el.name.match(/^items\[(\d+)\]/);
      if (match) indices.add(match[1]);
    });

    if (indices.size > 0) {
      const submitter = form.querySelector('[type="submit"]');
      const metadata = readItemMetadata(form, submitter);
      return Array.from(indices)
        .map((index) => {
          const properties = {};
          for (const [key, value] of fd.entries()) {
            const propMatch = key.match(new RegExp(`^items\\[${index}\\]\\[properties\\]\\[(.+)\\]$`));
            if (propMatch) properties[propMatch[1]] = value;
          }

          return {
            id: fd.get(`items[${index}][id]`),
            quantity: fd.get(`items[${index}][quantity]`) || 1,
            properties,
            selling_plan_id: fd.get(`items[${index}][selling_plan]`) || fd.get(`items[${index}][selling_plan_id]`),
            ...metadata,
          };
        })
        .filter((item) => item.id);
    }

    const properties = {};
    for (const [key, value] of fd.entries()) {
      const propMatch = key.match(/^properties\[(.+)\]$/);
      if (propMatch) properties[propMatch[1]] = value;
    }

    const variantId = fd.get("id");
    if (!variantId) return [];

    const submitter = form.querySelector('[type="submit"]');

    return [
      {
        id: variantId,
        quantity: resolveQuantity(form),
        properties,
        selling_plan_id: fd.get("selling_plan") || fd.get("selling_plan_id"),
        ...readItemMetadata(form, submitter),
      },
    ];
  }

  function openFakeCartDrawer(trigger) {
    const drawer = document.querySelector("fake-cart-drawer");
    if (drawer && typeof drawer.open === "function") {
      drawer.open(trigger || undefined);
    }
  }

  function triggerConfetti(button) {
    if (window.confetti && typeof window.confetti.burst === "function" && button) {
      window.confetti.burst(button);
    }
  }

  function addLineItems(lineItems, trigger) {
    for (let i = lineItems.length - 1; i >= 0; i -= 1) {
      window.FakeCart.addItem(lineItems[i]);
    }
    openFakeCartDrawer(trigger);
  }

  function handleFormSubmit(event) {
    const form = event.target;
    if (!isInterceptableForm(form)) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const lineItems = parseFormLineItems(form);
    if (lineItems.length === 0) return;

    const trigger = event.submitter || form.querySelector('[type="submit"]');
    addLineItems(lineItems, trigger);
    triggerConfetti(trigger);
  }

  function handleEyQtyClick(event) {
    const plus = event.target.closest(".ey-qty-plus");
    const minus = event.target.closest(".ey-qty-minus");
    if (!plus && !minus) return;

    const wrapper = event.target.closest(".ey-qty-cart");
    if (!wrapper || wrapper.classList.contains("ey-qty-cart--disabled")) return;
    if (wrapper.classList.contains("ey-qty-cart--loading")) return;

    const input = wrapper.querySelector(".ey-qty-input");
    const variantId = wrapper.dataset.productId;
    if (!variantId || !input) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const currentQty = parseInt(input.value, 10) || 0;
    const metadata =
      (window.FakeCartMetadata && FakeCartMetadata.readMetadata(wrapper)) || scrapeEyQtyMetadata(wrapper);
    if (!metadata.id) metadata.id = variantId;
    const trigger = plus || minus;

    if (plus) {
      window.FakeCart.addItem({ id: variantId, quantity: 1, ...metadata });
      input.value = String(currentQty + 1);
      triggerConfetti(trigger);
    } else {
      if (currentQty <= 0) return;
      window.FakeCart.adjustVariantQuantity(variantId, -1, metadata);
      input.value = String(Math.max(0, currentQty - 1));
    }

    // Match live PLP behavior (collection-grid-page.js): refresh cart state silently, do not open drawer.
  }

  document.addEventListener("submit", handleFormSubmit, true);
  document.addEventListener("click", handleEyQtyClick, true);

  window.FakeCartIntercept = {
    isInterceptableForm,
    parseFormLineItems,
    readItemMetadata,
    scrapeProductMetadata,
  };
})();
